import { useEffect, useMemo, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { BenachrichtigungsEinstellungen, PushGeraet } from "@kundeninfo/api-client";
import { useBestaetigung } from "@kundeninfo/ui";
import { Eingabe, Hinweis, Optionen, Schalter, Tabelle } from "@kundeninfo/ui/me-design";
import { KiKnopf, LoeschKnopf } from "../../components/meHuellen.js";
import { browserUmgebung, geraeteName, geraeteZustand, pushAbstimmen, pushAusschalten, pushEinschalten } from "./pushGeraet.js";
import type { GeraeteZustand, PushUmgebung } from "./pushGeraet.js";
import type { BenachrichtigungsEinstellungenClient } from "./benachrichtigungenClient.js";
import { NOTIFICATION_TEXT } from "./notificationTexts.js";

/**
 * EINSTELLUNGEN › BENACHRICHTIGUNGEN (Spec Tickets, Nachtrag, F105, F109): Ereignisarten,
 * Ruhezeit, dieses Gerät, meine Geräte. Eine abgeschaltete Art erzeugt gar keine
 * Benachrichtigung. Ereignisarten und Ruhezeit sind EIN Stand mit Version am Server
 * (Speichern/Abbrechen, 409 lädt neu); das Gerät schaltet sofort und nur nach einem Klick
 * (Entscheid 182: ausdrückliche Einwilligung je Gerät).
 */

const ZUSTAND_TEXT: Record<GeraeteZustand, string> = {
  NICHT_UNTERSTUETZT: "Dieser Browser kann keine Push-Mitteilungen empfangen. Auf dem iPhone und iPad geht Push nur, wenn das Kundeninfo zum Home-Bildschirm hinzugefügt ist (ab iOS 16.4) und von dort geöffnet wird.",
  NICHT_ERLAUBT: "Mitteilungen sind für diese Seite im Browser gesperrt — in den Website-Einstellungen des Browsers erlauben.",
  AUS: "Push-Mitteilungen sind auf diesem Gerät aus.",
  AN: "Push-Mitteilungen sind auf diesem Gerät an.",
};

/** Die Felder mit eigenem Eingabefeld — ein 422 an einem anderen Feld wird allgemein gemeldet. */
const BEKANNTE_FELDER = new Set(["ruhezeit.von", "ruhezeit.bis"]);

function gleich(a: BenachrichtigungsEinstellungen, b: BenachrichtigungsEinstellungen): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function EinstellungenBenachrichtigungen(p: { client: BenachrichtigungsEinstellungenClient; umgebung?: PushUmgebung }):
    ReactElement {
  const umgebung = useMemo(() => p.umgebung ?? browserUmgebung(), [p.umgebung]);
  /** Der zuletzt vom Server gelesene oder gespeicherte Stand — Ziel von „Abbrechen". */
  const [gespeichert, setGespeichert] = useState<BenachrichtigungsEinstellungen | null>(null);
  const [e, setE] = useState<BenachrichtigungsEinstellungen | null>(null);
  const [feldFehler, setFeldFehler] = useState<Record<string, string>>({});
  const [meldung, setMeldung] = useState<string | null>(null);
  const [ladeFehler, setLadeFehler] = useState(false);
  const [pushAus, setPushAus] = useState(false);
  const [zustand, setZustand] = useState<GeraeteZustand | null>(null);
  const [geraete, setGeraete] = useState<PushGeraet[]>([]);
  /** Die Kennung DIESES Geräts in der Liste: Die Liste nennt keine Endpunkte (die gehören
   *  nicht in eine Antwort); die Kennung kommt aus der Anmeldung beim Einschalten oder aus der
   *  Abstimmung beim Öffnen (siehe `pushAbstimmen`). */
  const [eigenesGeraet, setEigenesGeraet] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  /** Der Klient mit Merkzettel: was `registerPush` zurückgibt, ist dieses Gerät. */
  const merkender = useMemo<BenachrichtigungsEinstellungenClient>(() => ({
    ...p.client,
    registerPush: async (abo) => {
      const g = await p.client.registerPush(abo);
      setEigenesGeraet(g.id);
      return g;
    },
  }), [p.client]);

  // Vor dem frühen Rücksprung „Lädt …“ — ein Haken steht in jedem Zeichnen an derselben Stelle.
  const { bestaetigen } = useBestaetigung();

  function uebernimm(stand: BenachrichtigungsEinstellungen): void {
    setGespeichert(stand); setE(stand);
  }

  useEffect(() => {
    let aktiv = true;
    void p.client.getNotificationSettings().then((st) => { if (aktiv) uebernimm(st); })
      .catch(() => { if (aktiv) setLadeFehler(true); });
    void p.client.getPushKey().catch((f: unknown) => { if (aktiv && f instanceof ApiError && f.status === 404) setPushAus(true); });
    void (async () => {
      let z: GeraeteZustand;
      try { z = (await geraeteZustand(umgebung)).zustand; } catch { z = "NICHT_UNTERSTUETZT"; }
      if (!aktiv) return;
      setZustand(z);
      // Ein bestehendes Abo abstimmen, BEVOR die Liste kommt: Danach gehört die Zeile sicher
      // diesem Nutzer, und „dieses Gerät" ist bekannt.
      if (z === "AN") {
        try {
          const g = await pushAbstimmen(p.client, umgebung);
          if (aktiv && g !== null) setEigenesGeraet(g.id);
        } catch { /* Abstimmen ist Nebensache — die Seite bleibt bedienbar */ }
      }
      try {
        const r = await p.client.listPushDevices();
        if (aktiv) setGeraete(r.items);
      } catch { /* ohne Liste bleibt sie leer */ }
    })();
    return () => { aktiv = false; };
  }, [p.client, umgebung]);

  if (e === null || gespeichert === null) {
    return ladeFehler
      ? <Hinweis art="fehler">Die Einstellungen konnten nicht geladen werden.</Hinweis>
      : <p>Lädt …</p>;
  }
  const stand = e;
  const arten = [stand.notifyResubmit ? "resubmit" : null, stand.notifyComment ? "comment" : null,
    stand.notifyChange ? "change" : null].filter((x): x is string => x !== null);

  function ruhezeit(teil: Partial<BenachrichtigungsEinstellungen["ruhezeit"]>): void {
    setE({ ...stand, ruhezeit: { ...stand.ruhezeit, ...teil } });
  }
  function uhrzeit(feld: "von" | "bis", wert: string): void {
    setFeldFehler((f) => { const { [`ruhezeit.${feld}`]: _weg, ...rest } = f; return rest; });
    ruhezeit({ [feld]: wert });
  }

  async function speichern(): Promise<void> {
    setLaeuft(true); setFeldFehler({}); setMeldung(null);
    try { uebernimm(await p.client.putNotificationSettings(stand)); }
    catch (f) {
      if (f instanceof ApiError && f.status === 422) {
        const felder = f.fields ?? [];
        setFeldFehler(Object.fromEntries(felder.filter((x) => BEKANNTE_FELDER.has(x.field)).map((x) => [x.field, x.message])));
        if (felder.length === 0 || felder.some((x) => !BEKANNTE_FELDER.has(x.field))) {
          setMeldung("Die Einstellungen ließen sich so nicht speichern — bitte die Angaben prüfen.");
        }
      } else if (f instanceof ApiError && f.status === 409) {
        setMeldung("Die Einstellungen wurden inzwischen auf einem anderen Gerät geändert — der aktuelle Stand ist neu geladen.");
        try { uebernimm(await p.client.getNotificationSettings()); } catch { /* bleibt beim alten Stand */ }
      } else setMeldung("Speichern ist nicht gelungen.");
    } finally { setLaeuft(false); }
  }
  function abbrechen(): void {
    if (gespeichert !== null) setE(gespeichert);
    setFeldFehler({}); setMeldung(null);
  }

  async function geraetSchalten(an: boolean): Promise<void> {
    setLaeuft(true); setMeldung(null);
    try {
      if (an) setZustand(await pushEinschalten(merkender, umgebung));
      else { await pushAusschalten(p.client, umgebung); setEigenesGeraet(null); setZustand("AUS"); }
      setGeraete((await p.client.listPushDevices()).items);
    } catch { setMeldung("Das Gerät ließ sich nicht umschalten."); }
    finally { setLaeuft(false); }
  }
  async function entfernen(g: PushGeraet): Promise<void> {
    // Ein Löschweg — erst die Rückfrage (Hausregel „Bestätigung vor Datenänderung“, 19.10.2026).
    if (!(await bestaetigen({ titel: NOTIFICATION_TEXT.removeDeviceTitle(geraeteName(g.userAgent)),
      text: NOTIFICATION_TEXT.removeDeviceText, bestaetigenText: NOTIFICATION_TEXT.removeDevice }))) return;
    setMeldung(null);
    try {
      // Dieses Gerät: auch das Abo im Browser kündigen, sonst stünde es hier weiter auf „an".
      if (g.id === eigenesGeraet && zustand === "AN") {
        await pushAusschalten(p.client, umgebung); setEigenesGeraet(null); setZustand("AUS");
      } else await p.client.removePushDevice(g.id);
      setGeraete((l) => l.filter((x) => x.id !== g.id));
    } catch { setMeldung("Das Gerät ließ sich nicht entfernen."); }
  }

  const fehlerVon = (feld: string): { fehler?: string } =>
    feldFehler[feld] !== undefined ? { fehler: feldFehler[feld] } : {};

  return (
    <>
      <fieldset className="es__gruppe">
        <legend>Melden</legend>
        <Optionen mehrfach beschriftung="Ereignisarten" wert={arten}
          onGeaendert={(d) => { const w = d.werte ?? []; setE({ ...stand, notifyResubmit: w.includes("resubmit"),
            notifyComment: w.includes("comment"), notifyChange: w.includes("change") }); }}>
          <option value="resubmit">Termine fällig</option>
          <option value="comment">Kommentare</option>
          <option value="change">Änderungen</option>
        </Optionen>
      </fieldset>
      <fieldset className="es__gruppe">
        <legend>Ruhezeit</legend>
        <Schalter beschriftung="Ruhezeit einhalten (Push wartet, die Liste füllt sich sofort)" an={stand.ruhezeit.an}
          onGeaendert={(d) => ruhezeit({ an: d.an })} />
        {/* Ruhezeit aus: Uhrzeiten und Wochenende sind gesperrt (die Werte bleiben gespeichert und
            gelten wieder, sobald die Ruhezeit eingeschaltet wird). */}
        {/* me-eingabe art="uhrzeit" (F125): Zifferntastatur, rechtsbündig, normalisiert beim Verlassen
            auf „HH:MM"; der Server prüft wie bisher (422 am Feld `ruhezeit.von`/`ruhezeit.bis`). */}
        <Eingabe art="uhrzeit" beschriftung="Ruhezeit von"
          gesperrt={!stand.ruhezeit.an} wert={stand.ruhezeit.von} {...fehlerVon("ruhezeit.von")}
          onGeaendert={(d) => uhrzeit("von", d.wert)} />
        <Eingabe art="uhrzeit" beschriftung="Ruhezeit bis"
          gesperrt={!stand.ruhezeit.an} wert={stand.ruhezeit.bis} {...fehlerVon("ruhezeit.bis")}
          onGeaendert={(d) => uhrzeit("bis", d.wert)} />
        <Schalter beschriftung="Samstag und Sonntag ganztägig" an={stand.ruhezeit.wochenende} gesperrt={!stand.ruhezeit.an}
          onGeaendert={(d) => ruhezeit({ wochenende: d.an })} />
      </fieldset>
      {meldung !== null && <Hinweis art="warnung">{meldung}</Hinweis>}
      <div>
        <KiKnopf art="primaer" deaktiviert={laeuft || gleich(stand, gespeichert)} onClick={() => { void speichern(); }}>Speichern</KiKnopf>
        {" "}
        <KiKnopf deaktiviert={laeuft || gleich(stand, gespeichert)} onClick={abbrechen}>Abbrechen</KiKnopf>
      </div>
      <fieldset className="es__gruppe">
        <legend>Dieses Gerät</legend>
        {pushAus ? <Hinweis art="info">Push ist auf diesem System nicht eingerichtet.</Hinweis> : zustand !== null && (
          <Hinweis art={zustand === "AN" ? "ok" : zustand === "NICHT_ERLAUBT" ? "warnung" : "info"} aktionen={zustand === "AUS"
            ? <KiKnopf deaktiviert={laeuft} onClick={() => { void geraetSchalten(true); }}>Auf diesem Gerät einschalten</KiKnopf>
            : zustand === "AN"
              ? <KiKnopf deaktiviert={laeuft} onClick={() => { void geraetSchalten(false); }}>Auf diesem Gerät ausschalten</KiKnopf>
              : undefined}>
            {ZUSTAND_TEXT[zustand]}
          </Hinweis>
        )}
      </fieldset>
      <fieldset className="es__gruppe">
        <legend>Meine Geräte</legend>
        {geraete.length === 0 ? <p>Noch kein Gerät angemeldet.</p> : (
          <Tabelle stapeln aria-label="Meine Geräte">
            <table>
              <thead><tr><th>Gerät</th><th>seit</th><th>zuletzt erreicht</th><th aria-label="Aktion" /></tr></thead>
              <tbody>
                {geraete.map((g) => (
                  <tr key={g.id}>
                    <td>{geraeteName(g.userAgent)}{g.id === eigenesGeraet && zustand === "AN" ? " (dieses Gerät)" : ""}
                      {g.active ? "" : " (stillgelegt)"}</td>
                    <td>{new Date(g.createdAt).toLocaleDateString("de-DE")}</td>
                    <td>{g.lastSuccessAt === null ? "–" : new Date(g.lastSuccessAt).toLocaleString("de-DE")}</td>
                    <td><LoeschKnopf name={`Gerät ${geraeteName(g.userAgent)} entfernen`} deaktiviert={laeuft}
                      onClick={() => { void entfernen(g); }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Tabelle>
        )}
      </fieldset>
    </>
  );
}
